import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { BookingStep } from '@/utils/constants.js';
import { getConsentTreatments, getInsuranceAvailability } from '@/utils/helpers.js';
import { returnToSearchLanding } from '@/utils/search-navigation.ts';

import {
    CLEARED_CHAIN_SELECTION,
    useBookingStore,
    useConfigStore,
    usePersistedPrismicStore,
    useSearchStore,
} from '@/store.js';

import NoTreatmentsMessage from '../Common/NoTreatmentsMessage';
import ProfessionalIdentityInfo from '../Common/ProfessionalIdentityInfo.tsx';
import EternoSpinner from '../EternoLogoSpinner/eterno-spinner.tsx';
import FilledButton from '../FilledButton';
import TreatmentPill from './TreatmentPill.jsx';
import { SearchBackButton } from './search-back-button.tsx';

const useStyles = makeStyles((theme) => ({
    spinnerContainer: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
    },
    cardContentClasses: {
        display: 'block',
        position: 'sticky',
        top: 20,
    },
    professionalColumn: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    identityInfo: {
        width: '100%',
        padding: '10px 20px 0',
        boxSizing: 'border-box',
    },
    seeProfileWrap: {
        width: '-webkit-fill-available',
        paddingLeft: 20,
        paddingRight: 20,
        marginTop: 20,
    },
    backRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        width: '100%',
    },
    mainContentContainer: {
        display: 'flex',
        boxSizing: 'border-box',
        gap: '1rem',
        width: '100%',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    section: {
        display: 'flex',
        gap: '1rem',
        flexDirection: 'column',
        alignItems: 'start',
    },
    contentCard: {
        display: 'flex',
        flexDirection: 'column',
        width: '70%',
        padding: '30px 30px 60px 30px',
        borderLeft: theme.border.light,
        boxSizing: 'border-box',
        gap: '2rem',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            border: 'none',
        },
    },
    heading: {
        ...theme.typography.HL1,
        paddingBottom: 20,
    },
    treatmentsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        columnGap: '0.75rem',
        rowGap: '0.75rem',
        width: '100%',
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
        },
    },
}));

const BookWithProfessional = () => {
    const { i18n, t } = useTranslation();
    const classes = useStyles();
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [selectedProfessional, setSelectedProfessional] = useState(null);
    const [selectedSpecialty, setSelectedSpecialty] = useState(null);
    const [selectedTreatments, setSelectedTreatments] = useState({});
    const [filteredTreatments, setFilteredTreatments] = useState(null);
    const [isOnlineAppointment, setIsOnlineAppointment] = useState(false);
    const prismicData = useSearchStore((state) => state.prismicData);
    const showBookingModal = useBookingStore((state) => state.showBookingModal);
    const currentCustomer = useConfigStore((state) => state.currentCustomer);
    const professionals = useSearchStore((state) => state.professionals);
    const queryProfessional = useSearchStore((state) => state.selectedProfessional);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const allDocuments = useSearchStore((state) => state.allDocuments);
    const selectedHealthInsurance = useSearchStore((state) => state.selectedHealthInsurance);
    const treatments = useSearchStore((state) => state.treatments);

    const {
        [i18n.language]: {
            contentHeading: mainHeading,
            bookingHeading: mainBookingData,
            professionalProfiles: processedProfessionalProfiles,
            dataSet,
        },
    } = usePersistedPrismicStore((state) => state.findAppointmentData);

    const availableInsurances = useMemo(() => {
        if (!selectedProfessional || !selectedSpecialty) return [];

        const treatmentsArray = selectedProfessional.servicesList
            .filter((treatment) => !!treatments[selectedLocation][treatment.key[0].text])
            .map((treatment) => treatments[selectedLocation][treatment.key[0].text])
            .filter((treatment) => treatment.specialty === selectedSpecialty);

        const insuranceAvailability = getInsuranceAvailability(treatmentsArray);

        return Object.keys(insuranceAvailability).filter((key) => insuranceAvailability[key]);
    }, [selectedProfessional, selectedSpecialty]);

    const startBooking = (treatmentsToBook = selectedTreatments, isOnline = isOnlineAppointment) => {
        useBookingStore.getState().resetStore();
        useSearchStore.setState({
            selectedSpecialty: selectedSpecialty,
            selectedHealthInsurance: selectedHealthInsurance,
            selectedTreatments: treatmentsToBook,
            ...CLEARED_CHAIN_SELECTION,
            selectedLocation: selectedProfessional.locationData.key[0].text,
        });

        const consentTreatments = getConsentTreatments({
            selectedTreatments: treatmentsToBook,
            treatments: treatments,
            selectedLocation: selectedLocation,
            allDocuments: allDocuments,
            selectedHealthInsurance: selectedHealthInsurance,
        });

        useBookingStore.setState({
            selectedProfessional: selectedProfessional,
            isOnlineAppointment: isOnline,
            showBookingModal: true,
            consentTreatments: consentTreatments,
            currentStep: consentTreatments.length > 0 ? BookingStep.CONSENT : BookingStep.TIMESLOT_SELECTION,
        });
    };

    const updateSelectedTreatments = (treatment, isOnline = false) => {
        // With one treatment per booking there is nothing left to confirm, so picking one
        // opens the calendar straight away instead of waiting for a second click.
        if (!currentCustomer?.is_multi_treatment_booking_allowed) {
            const onlyTreatment = { [treatment]: true };
            setSelectedTreatments(onlyTreatment);
            setIsOnlineAppointment(isOnline);
            startBooking(onlyTreatment, isOnline);
            return;
        }

        // Reset selections if the appointment type changes
        if (isOnline !== isOnlineAppointment) {
            setSelectedTreatments({ [treatment]: true });
            setIsOnlineAppointment(isOnline);
            return;
        }

        // Toggle the treatment selection
        const newSelectedTreatments = { ...selectedTreatments };

        if (selectedTreatments[treatment]) {
            delete newSelectedTreatments[treatment];
        } else {
            newSelectedTreatments[treatment] = true;
        }

        setSelectedTreatments(newSelectedTreatments);
    };

    useEffect(() => {
        if (!currentCustomer || !professionals || !dataSet || !processedProfessionalProfiles?.length) return;

        const profile = processedProfessionalProfiles.find((p) => p.key[0].text === queryProfessional);

        if (
            !profile ||
            !currentCustomer.locations.some(
                (customerLocation) => customerLocation.key?.[0]?.text === profile.locationData?.key?.[0]?.text
            )
        ) {
            history.replace('/');
            useSearchStore.setState({ selectedProfessional: null });
            return;
        }

        setSelectedProfessional(profile);
        useSearchStore.setState({ selectedLocation: profile.locationData.key[0].text });
        setSelectedSpecialty(profile.specialityType.key[0].text);
        setLoading(false);
    }, [queryProfessional, currentCustomer, professionals, dataSet, processedProfessionalProfiles, i18n.language]);

    useEffect(() => {
        if (!selectedSpecialty) {
            setFilteredTreatments([]);
            return;
        }

        const newFilteredTreatments = selectedProfessional?.servicesList
            .filter((treatment) => !!treatments[selectedLocation][treatment.key[0].text])
            .map((treatment) => ({
                ...treatments[selectedLocation][treatment.key[0].text],
                name: treatment.name,
                public: treatments[selectedLocation][treatment.key[0].text].public_insurance,
                private: treatments[selectedLocation][treatment.key[0].text].private_insurance,
            }))
            .filter((treatment) => treatment.specialty === selectedSpecialty && treatment[selectedHealthInsurance]);

        setFilteredTreatments(newFilteredTreatments);
        setSelectedTreatments({});
    }, [selectedSpecialty, selectedHealthInsurance, selectedProfessional, treatments, selectedLocation]);

    // Location (in-person) and online treatment variants render identically apart from the online
    // pill and which appointment mode marks them selected.
    const renderTreatmentCard = (treatment, isOnline) => {
        const treatmentKey = treatment.key[0].text;
        const isSelected = selectedTreatments[treatmentKey] && isOnlineAppointment === isOnline;

        return (
            <TreatmentPill
                key={`${treatmentKey}-${isOnline ? 'online' : 'location'}`}
                dataTestId={`search-treatment-${treatmentKey}${isOnline ? '-online' : ''}`}
                label={treatment.name[0].text}
                isOnline={isOnline}
                selected={Boolean(isSelected)}
                onClick={() => updateSelectedTreatments(treatmentKey, isOnline)}
                asRow
            />
        );
    };

    if (loading || !selectedProfessional) {
        // The modal backdrop is translucent, so a spinner here would show through next to the modal's own.
        if (showBookingModal) return null;
        return (
            <div className={classes.spinnerContainer}>
                <EternoSpinner />
            </div>
        );
    }

    return (
        <div className={classes.backRow}>
            <SearchBackButton onClick={() => returnToSearchLanding(history)} />
            <div className={classes.mainContentContainer}>
                <Grid item md={4} sm={5} xs={12}>
                    <div className={classes.cardContentClasses}>
                        <div className={classes.professionalColumn}>
                            <ProfessionalIdentityInfo
                                variant="card"
                                className={classes.identityInfo}
                                imageUrl={selectedProfessional.profile_picture.url}
                                imageAlt={selectedProfessional.profile_picture.alt}
                                name={selectedProfessional.display_name[0].text}
                                professionText={selectedProfessional.specialityType?.name?.[0]?.text}
                                practiceName={selectedProfessional.practice_name?.[0]?.text}
                                languages={selectedProfessional.languages}
                                infoRows={[
                                    {
                                        icon: <LocationOnIcon />,
                                        text: selectedProfessional.locationData?.address?.[0]?.text,
                                    },
                                ]}
                            />
                            <div className={classes.seeProfileWrap}>
                                <FilledButton
                                    text={mainBookingData?.see_profile_cta?.[0]?.text || t('SeeProfile')}
                                    onPress={() => history.push(`/professional/${selectedProfessional.key[0].text}`)}
                                    fullWidth
                                    dataTestId="search-see-profile-btn"
                                />
                            </div>
                        </div>
                    </div>
                </Grid>
                <div className={classes.contentCard}>
                    {selectedSpecialty &&
                        (filteredTreatments.length > 0 ? (
                            <div className={classes.section}>
                                <Typography className={classes.heading}>{mainHeading[0].sub_heading_6}</Typography>
                                <div className={classes.treatmentsGrid}>
                                    {filteredTreatments.map((treatment) => (
                                        <Fragment key={treatment.key[0].text}>
                                            {treatment.location && renderTreatmentCard(treatment, false)}
                                            {treatment.online && renderTreatmentCard(treatment, true)}
                                        </Fragment>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            availableInsurances.length > 0 && (
                                <div className={classes.section}>
                                    <NoTreatmentsMessage
                                        selectedHealthInsurance={selectedHealthInsurance}
                                        publicText={prismicData.no_treatments_available_for_public_insurance[0].text}
                                        privateText={prismicData.no_treatments_available_for_private_insurance[0].text}
                                        selfPayText={prismicData.no_treatments_available_for_self_payer[0].text}
                                        changeInsuranceText={prismicData.change_insurance_to_see_treatments[0].text}
                                        availableInsurances={availableInsurances}
                                    />
                                </div>
                            )
                        ))}
                    {selectedSpecialty && Object.keys(selectedTreatments).length > 0 && (
                        <FilledButton
                            onPress={startBooking}
                            text={mainHeading[0].select_a_time}
                            dataTestId="search-select-time-btn"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookWithProfessional;
